'use client';

import { useTheme } from "@/providers/ThemeProvider";
import { useMemo } from 'react';

/**
 * Collection of gradient and color utilities for the tour UI
 * Optimized with memoization and pre-computed values
 */

// Pre-defined gradients for light mode
const LIGHT_GRADIENTS = {
  button: {
    primary: "bg-linear-to-r from-[#C5E6FF] via-[#A3EBAA] to-[#EFE883] hover:from-[#B0D6FF] hover:via-[#8EDBA0] hover:to-[#E5DD70]",
    secondary: "bg-linear-to-r from-blue-100 via-teal-100 to-emerald-100 hover:from-blue-200 hover:via-teal-200 hover:to-emerald-200",
    tertiary: "bg-linear-to-r from-amber-100 via-orange-100 to-pink-100 hover:from-amber-200 hover:via-orange-200 hover:to-pink-200"
  },
  card: {
    primary: "bg-linear-to-br from-white to-gray-50",
    purple: "bg-linear-to-br from-purple-50 to-indigo-100",
    blue: "bg-linear-to-br from-blue-50 to-cyan-100",
    green: "bg-linear-to-br from-emerald-50 to-teal-100"
  },
  icon: {
    purple: "bg-linear-to-br from-purple-200 to-indigo-300 text-indigo-800",
    blue: "bg-linear-to-br from-blue-200 to-cyan-300 text-blue-800",
    green: "bg-linear-to-br from-emerald-200 to-teal-300 text-emerald-800",
    pink: "bg-linear-to-br from-pink-200 to-rose-300 text-pink-800",
    amber: "bg-linear-to-br from-amber-200 to-orange-300 text-amber-800"
  }
};

// Pre-defined gradients for dark mode
const DARK_GRADIENTS = {
  button: {
    primary: "bg-linear-to-r from-indigo-500 via-purple-500 to-pink-500 hover:from-indigo-600 hover:via-purple-600 hover:to-pink-600",
    secondary: "bg-linear-to-r from-blue-500 via-teal-500 to-emerald-500 hover:from-blue-600 hover:via-teal-600 hover:to-emerald-600",
    tertiary: "bg-linear-to-r from-amber-500 via-orange-500 to-pink-500 hover:from-amber-600 hover:via-orange-600 hover:to-pink-600"
  },
  card: {
    primary: "bg-linear-to-br from-gray-900 to-gray-800",
    purple: "bg-linear-to-br from-purple-900 to-indigo-900",
    blue: "bg-linear-to-br from-blue-900 to-cyan-900",
    green: "bg-linear-to-br from-emerald-900 to-teal-900"
  },
  icon: {
    purple: "bg-linear-to-br from-purple-500 to-indigo-600 text-white",
    blue: "bg-linear-to-br from-blue-500 to-cyan-600 text-white",
    green: "bg-linear-to-br from-emerald-500 to-teal-600 text-white",
    pink: "bg-linear-to-br from-pink-500 to-rose-600 text-white",
    amber: "bg-linear-to-br from-amber-500 to-orange-600 text-white"
  }
};

// Pre-computed overlay colors
const LIGHT_OVERLAY_COLORS = [
  { rgb: "76, 29, 149", opacity: "0.6" }, // Purple
  { rgb: "30, 64, 175", opacity: "0.6" }, // Blue
  { rgb: "4, 120, 87", opacity: "0.6" },  // Green
  { rgb: "157, 23, 77", opacity: "0.6" }, // Pink
  { rgb: "180, 83, 9", opacity: "0.6" }   // Amber
];

const DARK_OVERLAY_COLORS = [
  { rgb: "76, 29, 149", opacity: "0.75" }, // Purple
  { rgb: "30, 64, 175", opacity: "0.75" }, // Blue
  { rgb: "4, 120, 87", opacity: "0.75" },  // Green
  { rgb: "157, 23, 77", opacity: "0.75" }, // Pink
  { rgb: "180, 83, 9", opacity: "0.75" }   // Amber
];

// Get dynamic gradient backgrounds based on step index and theme - optimized with useMemo
export const useStepGradients = () => {
  const { isDarkMode } = useTheme();

  // Memoize gradients based on theme
  const gradients = useMemo(() =>
    isDarkMode ? DARK_GRADIENTS : LIGHT_GRADIENTS,
    [isDarkMode]
  );

  // Get a gradient based on step index to cycle through colors - optimized lookup
  const getStepGradient = useMemo(() => (type, stepIndex) => {
    if (type === 'button') {
      const buttonGradients = [gradients.button.primary, gradients.button.secondary, gradients.button.tertiary];
      return buttonGradients[stepIndex % 3]; // Using direct length for performance
    }

    if (type === 'card') {
      const cardGradients = [gradients.card.primary, gradients.card.purple, gradients.card.blue, gradients.card.green];
      return cardGradients[stepIndex % 4];
    }

    if (type === 'icon') {
      const iconGradients = [
        gradients.icon.purple,
        gradients.icon.blue,
        gradients.icon.green,
        gradients.icon.pink,
        gradients.icon.amber
      ];
      return iconGradients[stepIndex % 5];
    }

    return '';
  }, [gradients]);

  return {
    gradients,
    getStepGradient
  };
};

// Dynamic overlay colors for different steps - optimized lookup
export const getOverlayColor = (stepIndex, isDarkMode) => {
  const overlayColors = isDarkMode ? DARK_OVERLAY_COLORS : LIGHT_OVERLAY_COLORS;
  // Fast modulo calculation with direct array access
  return overlayColors[stepIndex % 5];
};

// Pre-defined animation presets to avoid recreation
export const animations = {
  // Card animations
  card: {
    // Standard card entrance/exit
    standard: {
      hidden: { opacity: 0, y: 15, scale: 0.95 },
      visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: {
          duration: 0.25,
          ease: "easeOut"
        }
      },
      exit: {
        opacity: 0,
        y: -15,
        scale: 0.95,
        transition: { duration: 0.2 }
      }
    },

    // More dramatic animation for intro
    intro: {
      hidden: { opacity: 0, scale: 0.8, y: 20 },
      visible: {
        opacity: 1,
        scale: 1,
        y: 0,
        transition: {
          type: "spring",
          stiffness: 400,
          damping: 25,
          duration: 0.5
        }
      },
      exit: {
        opacity: 0,
        scale: 0.9,
        y: -10,
        transition: { duration: 0.3 }
      }
    },

    // Slide from side
    slide: {
      hidden: { opacity: 0, x: 30 },
      visible: {
        opacity: 1,
        x: 0,
        transition: {
          type: "spring",
          stiffness: 300,
          damping: 25
        }
      },
      exit: {
        opacity: 0,
        x: -30,
        transition: { duration: 0.3 }
      }
    }
  },

  // Content animations
  content: {
    standard: {
      hidden: { opacity: 0, y: 10 },
      visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.3, delay: 0.1 }
      }
    },

    // Use function factory pattern for better performance
    staggered: (index) => ({
      hidden: { opacity: 0, y: 15 },
      visible: {
        opacity: 1,
        y: 0,
        transition: {
          duration: 0.3,
          delay: 0.1 + (index * 0.1)
        }
      }
    })
  },

  // Button animations
  button: {
    pulse: {
      animate: {
        scale: [1, 1.05, 1],
        transition: {
          duration: 1.5,
          repeat: Infinity,
          repeatType: "reverse"
        }
      }
    },

    hover: {
      scale: 1.05,
      transition: { duration: 0.2 }
    },

    tap: {
      scale: 0.95,
      transition: { duration: 0.1 }
    }
  }
};
